import { createBaselineProgress, pilotSkillLabels, pilotSkillOrder, prerequisitesMet } from './catalog';
import type { PilotQuestionInstance, PilotSkillProgress } from './types';

type Rng = () => number;

type PlanSlot = {
  skillCode: string;
  reasonCode: PilotQuestionInstance['reasonCode'];
  difficultyBand: PilotQuestionInstance['difficultyBand'];
};

function mulberry32(seed: number): Rng {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function int(rng: Rng, min: number, max: number) {
  return Math.floor(rng() * (max - min + 1)) + min;
}

function digitOnes(value: number) { return value % 10; }
function digitTens(value: number) { return Math.floor(value / 10); }

function idFor(index: number, seed: number) {
  return `QI-PILOT-${seed.toString(16)}-${String(index + 1).padStart(2, '0')}`;
}

function difficultyFor(mastery: number, reason: PlanSlot['reasonCode']): PlanSlot['difficultyBand'] {
  if (reason === 'remediation') return mastery < 55 ? 'D1' : 'D2';
  if (reason === 'mastery_check') return 'D3';
  if (mastery < 60) return 'D1';
  if (mastery < 75) return 'D2';
  if (mastery < 88) return 'D3';
  return 'D3';
}

function cyclicTake(values: string[], count: number): string[] {
  if (values.length === 0 || count <= 0) return [];
  return Array.from({ length: count }, (_, index) => values[index % values.length]);
}

export function buildAdaptivePilotPlan(progress: Record<string, PilotSkillProgress>): PlanSlot[] {
  const unlocked = pilotSkillOrder.filter((skillCode) => prerequisitesMet(skillCode, progress));
  const byMasteryAsc = [...unlocked].sort((a, b) => (progress[a]?.masteryPercent ?? 0) - (progress[b]?.masteryPercent ?? 0));
  const byMasteryDesc = [...unlocked].sort((a, b) => (progress[b]?.masteryPercent ?? 0) - (progress[a]?.masteryPercent ?? 0));

  const review = byMasteryDesc.filter((skillCode) => (progress[skillCode]?.masteryPercent ?? 0) >= 85).slice(0, 2);
  const remediation = byMasteryAsc.filter((skillCode) => (progress[skillCode]?.masteryPercent ?? 0) < 65).slice(0, 2);
  const current = byMasteryAsc.filter((skillCode) => {
    const mastery = progress[skillCode]?.masteryPercent ?? 0;
    return mastery >= 65 && mastery < 85;
  });
  const masteryCheck = byMasteryDesc.find((skillCode) => {
    const mastery = progress[skillCode]?.masteryPercent ?? 0;
    return mastery >= 80 && mastery < 90;
  });

  const slots: PlanSlot[] = [];
  for (const skillCode of review) slots.push({ skillCode, reasonCode: 'spaced_review', difficultyBand: difficultyFor(progress[skillCode]?.masteryPercent ?? 0, 'spaced_review') });
  for (const skillCode of remediation) slots.push({ skillCode, reasonCode: 'remediation', difficultyBand: difficultyFor(progress[skillCode]?.masteryPercent ?? 0, 'remediation') });

  const currentSlots = cyclicTake(current.length > 0 ? current : byMasteryAsc, Math.max(0, 9 - slots.length));
  for (const skillCode of currentSlots) slots.push({ skillCode, reasonCode: 'current_practice', difficultyBand: difficultyFor(progress[skillCode]?.masteryPercent ?? 0, 'current_practice') });

  if (masteryCheck) {
    slots.push({ skillCode: masteryCheck, reasonCode: 'mastery_check', difficultyBand: 'D3' });
  } else if (byMasteryDesc[0]) {
    slots.push({ skillCode: byMasteryDesc[0], reasonCode: 'spaced_review', difficultyBand: 'D3' });
  }

  while (slots.length < 10 && byMasteryAsc.length > 0) {
    const skillCode = byMasteryAsc[slots.length % byMasteryAsc.length];
    slots.push({ skillCode, reasonCode: 'current_practice', difficultyBand: difficultyFor(progress[skillCode]?.masteryPercent ?? 0, 'current_practice') });
  }

  return slots.slice(0, 10);
}

function generateForSkill(slot: PlanSlot, rng: Rng, index: number, seed: number): PilotQuestionInstance {
  const common = {
    id: idFor(index, seed),
    skillCode: slot.skillCode,
    skillLabel: pilotSkillLabels[slot.skillCode],
    reasonCode: slot.reasonCode,
    difficultyBand: slot.difficultyBand,
    generationSeed: seed,
    generatorVersion: 'pilot-math-1.1.0' as const,
  };

  if (slot.skillCode === 'BK-MTH-NUM-PV-002') {
    return { ...common, prompt: '10 satuan = ? puluhan', answer: 1, presentation: 'concept' };
  }

  if (slot.skillCode === 'BK-MTH-NUM-PV-003') {
    const n = int(rng, 21, 98);
    return { ...common, prompt: `${n} terdiri dari berapa puluhan?`, supportingText: 'Fokus pada nilai tempat puluhan.', answer: digitTens(n), presentation: 'concept' };
  }

  if (slot.skillCode === 'BK-MTH-NUM-PV-005') {
    const tens = int(rng, 1, 9);
    const ones = int(rng, 0, 9);
    return { ...common, prompt: `${tens} puluhan + ${ones} satuan = ?`, answer: (tens * 10) + ones, presentation: 'concept' };
  }

  if (slot.skillCode === 'BK-MTH-NUM-PV-006') {
    const n = int(rng, 21, 89);
    const tensValue = digitTens(n) * 10;
    return { ...common, prompt: `${n} = ${tensValue} + ?`, answer: digitOnes(n), presentation: 'concept' };
  }

  if (slot.skillCode === 'BK-MTH-OPS-ADD-021') {
    let a = 0; let b = 0;
    do {
      a = int(rng, 11, 98);
      b = int(rng, 1, 9);
    } while (digitOnes(a) + b > 9);
    return { ...common, prompt: `${a} + ${b}`, answer: a + b, presentation: slot.difficultyBand === 'D1' ? 'vertical' : 'horizontal' };
  }

  if (slot.skillCode === 'BK-MTH-OPS-ADD-022') {
    let a = 0; let b = 0;
    do {
      a = int(rng, 11, 78);
      b = int(rng, 11, 78);
    } while (digitOnes(a) + digitOnes(b) > 9 || digitTens(a) + digitTens(b) > 9);
    return { ...common, prompt: `${a} + ${b}`, answer: a + b, presentation: 'vertical' };
  }

  if (slot.skillCode === 'BK-MTH-OPS-ADD-023') {
    const onesA = int(rng, 5, 9);
    const onesB = int(rng, 10 - onesA, 9);
    const total = onesA + onesB;
    return {
      ...common,
      prompt: `${total} satuan dapat dikelompokkan menjadi berapa puluhan?`,
      supportingText: `${onesA} satuan + ${onesB} satuan = ${total} satuan`,
      answer: Math.floor(total / 10),
      presentation: 'concept',
    };
  }

  if (slot.skillCode === 'BK-MTH-OPS-ADD-025') {
    let a = 0; let b = 0;
    do {
      a = int(rng, 21, 78);
      b = int(rng, 21, 78);
    } while (digitOnes(a) + digitOnes(b) < 10 || digitTens(a) + digitTens(b) + 1 > 9);
    return { ...common, prompt: `${a} + ${b}`, answer: a + b, presentation: slot.difficultyBand === 'D1' || slot.difficultyBand === 'D2' ? 'vertical' : 'horizontal' };
  }

  if (slot.skillCode === 'BK-MTH-OPS-SUB-021') {
    let a = 0; let b = 0;
    do {
      a = int(rng, 11, 98);
      b = int(rng, 1, 9);
    } while (digitOnes(a) < b);
    return { ...common, prompt: `${a} − ${b}`, answer: a - b, presentation: slot.difficultyBand === 'D1' ? 'vertical' : 'horizontal' };
  }

  if (slot.skillCode === 'BK-MTH-OPS-SUB-022') {
    let a = 0; let b = 0;
    do {
      a = int(rng, 31, 98);
      b = int(rng, 11, a - 1);
    } while (digitOnes(a) < digitOnes(b) || digitTens(a) < digitTens(b));
    return { ...common, prompt: `${a} − ${b}`, answer: a - b, presentation: 'vertical' };
  }

  if (slot.skillCode === 'BK-MTH-OPS-SUB-023') {
    const onesAvailable = int(rng, 0, 5);
    const onesNeeded = int(rng, onesAvailable + 1, 9);
    return {
      ...common,
      prompt: `Jika satuannya ${onesAvailable}, tetapi kita perlu mengurangi ${onesNeeded} satuan, 1 puluhan harus ditukar menjadi berapa satuan?`,
      answer: 10,
      presentation: 'concept',
    };
  }

  if (slot.skillCode === 'BK-MTH-OPS-SUB-025') {
    let a = 0; let b = 0;
    do {
      a = int(rng, 31, 98);
      b = int(rng, 12, a - 1);
    } while (digitOnes(a) >= digitOnes(b));
    return { ...common, prompt: `${a} − ${b}`, answer: a - b, presentation: 'vertical' };
  }

  throw new Error(`Unsupported pilot skill: ${slot.skillCode}`);
}

export function createSecureSeed(): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const buffer = new Uint32Array(1);
    crypto.getRandomValues(buffer);
    return buffer[0] || 1;
  }
  return Math.max(1, Math.floor(Math.random() * 0xFFFFFFFF));
}

export function generatePilotQuestions(seed: number, progress: Record<string, PilotSkillProgress> = createBaselineProgress()): PilotQuestionInstance[] {
  const rng = mulberry32(seed);
  const plan = buildAdaptivePilotPlan(progress);
  return plan.map((slot, index) => generateForSkill(slot, rng, index, seed));
}
